import { defineLocalStore } from '@/utils/localStore'
import { computed, ref } from 'vue'
import { cachedObject, CustomDate } from 'best-modules/utils'
import { handleAsync } from 'best-modules/plugins'
import { required, useBreadcrumb, useRoute, useVuelidate } from '@/plugins'
import { openDialog } from '@/dialog'
import {
  contractorAddressDelete,
  contractorEmailDelete,
  contractorPhoneDelete,
  contractorSingle,
  contractorUpdate,
} from '@/requests/contractor'
import { getAddressStr, notifyValidation } from '@/utils/helpers'
import { useCan } from '@/composables/useCan'
import type {
  Contractor,
  ContractorContact,
  ContractorContactConfig,
  ContractorContactType,
} from '@/utils/types/contractor'

export const {
  useLocalStore: useContractorStore,
  initLocalStore: initContractorStore,
} = defineLocalStore('contractorSingle', () => {
  const route = useRoute()
  const breadcrumb = useBreadcrumb()
  const { can } = useCan()
  const activeTab = ref('general')
  const contractor = cachedObject<Contractor>({} as Contractor)
  contractor.$setIgnore(
    'addresses',
    'emails',
    'phones',
    'created_at',
    'updated_at'
  )

  const contractorId = computed(() => Number(route.params.id))
  const readonly = computed(() => !can('contractors.update'))
  const statusLabel = computed(() => 'Активний')
  const typeName = computed(() => contractor.type?.name || '')
  const initials = computed(() => {
    const source = contractor.shortName || contractor.name || ''
    const letters = source
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((item: string) => item[0])
      .join('')

    return letters || 'К'
  })
  const createdAt = computed(() => {
    if (!contractor.created_at) return ''

    return new CustomDate(contractor.created_at).toString()
  })

  const tabs = computed(() => [
    { value: 'general', label: 'Загальне' },
    {
      value: 'addresses',
      label: 'Адреси',
      count: contractor.addresses?.length || 0,
    },
    { value: 'contacts', label: 'Контакти', count: contactCount.value },
    { value: 'links', label: "Зв'язки", count: linksCount.value },
    { value: 'history', label: 'Історія' },
  ])

  const contactCount = computed(
    () => (contractor.phones?.length || 0) + (contractor.emails?.length || 0)
  )
  const documentLinks = computed(() => contractor.documents || [])
  const taskLinks = computed(() => contractor.tasks || [])
  const linksCount = computed(
    () => documentLinks.value.length + taskLinks.value.length
  )

  const rules = {
    name: { required },
    code: { required },
    typeId: { required },
    shortName: { required },
  }
  const v$ = useVuelidate(rules, contractor)

  const contactConfigs: Record<ContractorContactType, ContractorContactConfig> =
    {
      address: {
        addLabel: 'Додати адресу',
        columnLabel: 'Адреса',
        delete: contractorAddressDelete,
        dialogTitle: 'адресу',
        emptyTitle: 'Адреси відсутні',
        listKey: 'addresses',
        title: 'Адреси контрагента',
        valueKey: 'address',
      },
      phone: {
        addLabel: 'Додати телефон',
        columnLabel: 'Номер',
        delete: contractorPhoneDelete,
        dialogTitle: 'телефон',
        emptyTitle: 'Телефони відсутні',
        listKey: 'phones',
        title: 'Телефони',
        valueKey: 'phone',
      },
      email: {
        addLabel: 'Додати email',
        columnLabel: 'Адреса',
        delete: contractorEmailDelete,
        dialogTitle: 'email',
        emptyTitle: 'Email відсутні',
        listKey: 'emails',
        title: 'Email',
        valueKey: 'email',
      },
    }

  const setContractor = (obj: Contractor) => {
    contractor.$set(obj || {})
    breadcrumb.set([
      {
        title: 'Контрагенти',
        to: { name: 'contractors' },
        index: 0,
      },
      {
        title: obj?.shortName || obj?.name || 'Контрагент',
        index: 1,
      },
    ])
  }

  const getContractor = () => {
    return handleAsync('getContractor', () => {
      return contractorSingle(contractorId.value).then(res => {
        setContractor(res)
      })
    })
  }

  const updateContractor = () => {
    v$.value.$touch()
    if (v$.value.$invalid) {
      notifyValidation(v$.value)
      return
    }

    return contractorUpdate(contractorId.value, contractor.$object).then(
      res => {
        setContractor(res)
      }
    )
  }

  const openContractorInfoDialog = () => {
    openDialog({
      name: 'ContractorDetails',
      dialogData: {
        contractor: contractor.$object,
        title: `Інформація про контрагента - ${contractor.code}`,
      },
      params: { hideActionButtons: true },
    })
  }

  const openContactDialog = (
    type: ContractorContactType,
    contact?: ContractorContact
  ) => {
    const config = contactConfigs[type]

    openDialog({
      name: 'ContractorContact',
      action: contact ? 'update' : 'create',
      dialogData: {
        contact,
        contractorId: contractor.id,
        title: contact ? config.title : config.dialogTitle,
        type,
      },
      params: {
        cb: (newContact: ContractorContact) => {
          const contacts = [...(contractor[config.listKey] || [])]
          const index = contacts.findIndex(item => item.id === contact?.id)

          if (index >= 0) {
            contacts.splice(index, 1, newContact)
          } else {
            contacts.push(newContact)
          }

          contractor[config.listKey] = contacts
        },
      },
    })
  }

  const deleteContact = (type: ContractorContactType, contactId: number) => {
    const config = contactConfigs[type]

    return config.delete(contactId).then(() => {
      contractor[config.listKey] = (contractor[config.listKey] || []).filter(
        (contact: ContractorContact) => contact.id !== contactId
      )
    })
  }

  const contactValue = (
    type: ContractorContactType,
    contact: ContractorContact
  ) => {
    if (type === 'address') return getAddressStr(contact) || contact.address

    return contact[type]
  }

  getContractor()

  return {
    activeTab,
    can,
    contactConfigs,
    contactCount,
    contactValue,
    contractor,
    createdAt,
    deleteContact,
    documentLinks,
    getContractor,
    initials,
    openContactDialog,
    openContractorInfoDialog,
    readonly,
    statusLabel,
    tabs,
    taskLinks,
    typeName,
    updateContractor,
    v$,
  }
})

export type ContractorStore = ReturnType<typeof useContractorStore>
